import API_BASE from '../config';

// Prevent multiple simultaneous auth-error redirects
let _redirectingToLogin = false;

const FETCH_TIMEOUT_MS = 30000; // 30-second timeout for all API calls

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const handleAuthError = (response) => {
  if (response.status === 401 || response.status === 403) {
    if (!_redirectingToLogin) {
      _redirectingToLogin = true;
      localStorage.removeItem('token');
      window.location.replace('/login');
    }
    throw new Error('Session expired');
  }
};

/**
 * Fetch wrapper with timeout. Rejects if the request takes longer than FETCH_TIMEOUT_MS.
 */
const fetchWithTimeout = (url, options = {}) => {
  const controller = new AbortController();
  const existingSignal = options.signal;

  // If caller provided their own signal, listen for its abort too
  if (existingSignal) {
    existingSignal.addEventListener('abort', () => controller.abort());
  }

  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  return fetch(url, { ...options, signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
  });
};

const parseError = (data, fallback) => {
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  return fallback;
};

export const api = {
  sendMessage: async (question, conversationId = null, signal = null) => {
    const fetchOpts = {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ question, conversation_id: conversationId }),
    };
    if (signal) fetchOpts.signal = signal;
    const response = await fetchWithTimeout(`${API_BASE}/chat/ask`, fetchOpts);
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to send message'));
    return data;
  },

  getChatHistory: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/chat/history`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to load history'));
    return data;
  },

  deleteConversation: async (conversationId) => {
    const response = await fetchWithTimeout(`${API_BASE}/chat/history/${conversationId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to delete conversation'));
    return data;
  },

  getAdminStats: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/stats`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Admin access denied');
    return response.json();
  },

  getAllUsers: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to load users');
    return response.json();
  },

  createUser: async (userData) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to create user'));
    return data;
  },

  deleteUser: async (userId) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to delete user'));
    return data;
  },

  updateUser: async (userId, userData) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to update user'));
    return data;
  },

  getAllConversations: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/conversations`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to load conversations');
    return response.json();
  },

  getDashboardData: async (timescale) => {
    const params = new URLSearchParams();
    if (timescale) params.append('timescale', timescale);

    const response = await fetchWithTimeout(`${API_BASE}/financial/summary?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to load dashboard');
    return response.json();
  },

  getFinancialDashboard: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/financial/dashboard`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to load financial dashboard');
    return response.json();
  },

  getMe: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/me`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Not authenticated');
    return response.json();
  },

  setupPassword: async (token, password) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/setup-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to set password'));
    return data;
  },

  verify2FA: async (email, code) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/verify-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Invalid code'));
    return data;
  },

  resend2FA: async (email) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/resend-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to resend code'));
    return data;
  },

  getAdminHealth: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/health`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to load health');
    return response.json();
  },

  // Phone linking
  linkPhone: async (phoneNumber) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/link-phone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to send verification code'));
    return data;
  },

  verifyPhone: async (code) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/verify-phone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code }),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Invalid verification code'));
    return data;
  },

  unlinkPhone: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/unlink-phone`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to unlink phone'));
    return data;
  },

  // Report & alert preferences
  updateReportPreferences: async (preferences) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/report-preferences`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(preferences),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to update preferences'));
    return data;
  },



  // Token refresh
  refreshToken: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to refresh token'));
    if (data.access_token) {
      localStorage.setItem('token', data.access_token);
    }
    return data;
  },

  // Test summary
  testSummary: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/test-summary`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to send test summary'));
    return data;
  },

  // Test alert
  testAlert: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/test-alert`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to send test alert'));
    return data;
  },

  // WhatsApp status
  getWhatsAppStatus: async () => {
    const response = await fetchWithTimeout(`${API_BASE}/whatsapp/status`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to get WhatsApp status');
    return response.json();
  },

  // Business switching
  switchBusiness: async (businessId) => {
    const response = await fetchWithTimeout(`${API_BASE}/auth/switch-business`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ business_id: businessId }),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to switch business'));
    return data;
  },

  // Business CRUD (admin)
  getUserBusinesses: async (userId) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users/${userId}/businesses`, {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to load businesses');
    return response.json();
  },

  addBusiness: async (userId, businessData) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users/${userId}/businesses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(businessData),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to add business'));
    return data;
  },

  updateBusiness: async (businessId, businessData) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/businesses/${businessId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(businessData),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to update business'));
    return data;
  },

  sendWelcome: async (userId) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/users/${userId}/send-welcome`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to send welcome message'));
    return data;
  },

  deleteBusiness: async (businessId) => {
    const response = await fetchWithTimeout(`${API_BASE}/admin/businesses/${businessId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    const data = await response.json();
    if (!response.ok) throw new Error(parseError(data, 'Failed to delete business'));
    return data;
  },
};
